import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardBody, CardFooter, CardHeader, CardTitle } from "@hp/ui";
import type { AlertSettings } from "@hp/api-client";
import { updateAlertSettings } from "../api/alertSettings";
import { ErrorBlock } from "./QueryStatus";

const DEFAULT_TEMPERATURE = 0.7;
const MAX_TEMPERATURE = 2;

/** How HP Genie's answer-writing model behaves. Org-wide: it applies to everyone on the team. */
export function LookupModelForm({ settings }: { settings: AlertSettings }) {
  const queryClient = useQueryClient();
  const saved = settings.lookup_temperature ?? DEFAULT_TEMPERATURE;
  const [text, setText] = React.useState(String(saved));
  React.useEffect(() => setText(String(saved)), [saved]);
  const savedFeedback = settings.show_genie_feedback === true;
  const [showFeedback, setShowFeedback] = React.useState(savedFeedback);
  React.useEffect(() => setShowFeedback(savedFeedback), [savedFeedback]);

  const value = Number(text);
  const valid = text.trim() !== "" && Number.isFinite(value) && value >= 0 && value <= MAX_TEMPERATURE;

  const mutation = useMutation({
    mutationFn: updateAlertSettings,
    onSuccess: (updated) => queryClient.setQueryData(["alert-settings"], updated),
  });

  function handleSave(event: React.FormEvent) {
    event.preventDefault();
    if (valid) mutation.mutate({ lookup_temperature: value, show_genie_feedback: showFeedback });
  }

  return (
    <form onSubmit={handleSave}>
      <Card>
        <CardHeader>
          <CardTitle>HP Genie answers</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <label className="block text-sm text-zinc-800">
            <span className="font-medium">Temperature</span>
            <span className="mt-1 flex items-center gap-3">
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                className="w-56 accent-brand-600"
                value={valid ? Math.min(1, value) : saved}
                onChange={(event) => setText(String(Number(event.target.value)))}
                aria-label="Temperature slider"
              />
              <input
                type="number"
                min={0}
                max={MAX_TEMPERATURE}
                step={0.05}
                className="w-20 rounded-md border border-zinc-300 px-2 py-1 text-sm"
                value={text}
                onChange={(event) => setText(event.target.value)}
                aria-label="Temperature"
              />
              <button type="button" className="text-xs text-brand-700 underline" onClick={() => setText(String(DEFAULT_TEMPERATURE))}>
                Reset to {DEFAULT_TEMPERATURE}
              </button>
            </span>
            <span className="mt-1 block text-xs text-zinc-500">
              How much the model varies its wording when it writes an answer, from 0 to {MAX_TEMPERATURE}. A low value (0 to 0.2) gives nearly the same answer every time the same question is asked.
              A higher value gives more varied wording and, now and then, a different emphasis or a different figure picked out. Applies to the chat and to deep research. It takes effect on the
              next question, within about 30 seconds on every server.
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-sm text-zinc-800">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-brand-600"
              checked={showFeedback}
              onChange={(event) => setShowFeedback(event.target.checked)}
            />
            <span>
              <span className="font-medium">Show the Feedback button under answers</span>
              <span className="block text-xs text-zinc-500">
                Ticked: every HP Genie answer has a Feedback button (thumbs up or down and a comment). Each piece of feedback is saved with the question, the answer and its sources, and an admin reads them all under Admin &gt; Feedback.
                Unticked: the button is hidden (feedback already sent is kept).
              </span>
            </span>
          </label>
        </CardBody>
        <CardFooter className="flex flex-wrap items-center justify-between gap-3">
          <Button type="submit" disabled={mutation.isPending || !valid || (value === saved && showFeedback === savedFeedback)}>
            {mutation.isPending ? "Saving..." : "Save"}
          </Button>
          <span className="text-xs text-zinc-500">
            {mutation.isSuccess ? "Saved. " : ""}
            {!valid ? `Enter a number from 0 to ${MAX_TEMPERATURE}. ` : ""}Applies to everyone on the team. Only a reviewer or admin can change it.
          </span>
        </CardFooter>
        {mutation.isError ? <ErrorBlock message="Could not save. Only a reviewer or admin can change this." surface="canvas" /> : null}
      </Card>
    </form>
  );
}
